import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createSupplyClient } from './api';
import type {
  SupplyAppPayload,
  SupplyDomainPayload,
  SupplyListFilter,
  TailoredSupplyPayload,
} from './model';

const supplyQueryKey = ['campaigns', 'supply'] as const;

export enum SupplyQueryResource {
  Apps = 'apps',
  Domains = 'domains',
  Tailored = 'tailored',
  Platforms = 'platforms',
  IabCategories = 'iab-categories',
}

export function supplyResourceQueryKey(resource: SupplyQueryResource) {
  return [...supplyQueryKey, resource] as const;
}

function supplyResourceListsQueryKey(resource: SupplyQueryResource) {
  return [...supplyResourceQueryKey(resource), 'list'] as const;
}

function useSupplyClient() {
  const scope = useAgencyCampaignContext();
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  const client = createSupplyClient({
    config: {
      apiBaseUrl: config.madBuyServiceUrl,
      networkId: config.networkId,
    },
    accessToken: session.accessToken,
  });
  return {
    ...client,
    queryScope: scope ? ['agency-demand', config.networkId, scope.agency.id] : [],
  };
}

export function useSupplyAppsQuery(filter: SupplyListFilter, enabled = true) {
  const client = useSupplyClient();
  return useQuery({
    queryKey: [
      ...client.queryScope,
      ...supplyResourceListsQueryKey(SupplyQueryResource.Apps),
      filter,
    ],
    queryFn: () => client.listApps(filter),
    enabled,
  });
}
export function useSupplyDomainsQuery(filter: SupplyListFilter, enabled = true) {
  const client = useSupplyClient();
  return useQuery({
    queryKey: [
      ...client.queryScope,
      ...supplyResourceListsQueryKey(SupplyQueryResource.Domains),
      filter,
    ],
    queryFn: () => client.listDomains(filter),
    enabled,
  });
}
export function useTailoredSuppliesQuery(filter: SupplyListFilter, enabled = true) {
  const client = useSupplyClient();
  return useQuery({
    queryKey: [
      ...client.queryScope,
      ...supplyResourceListsQueryKey(SupplyQueryResource.Tailored),
      filter,
    ],
    queryFn: () => client.listTailoredSupplies(filter),
    enabled,
  });
}
export function useTailoredSupplyQuery(id: string | undefined) {
  const client = useSupplyClient();
  return useQuery({
    queryKey: [
      ...client.queryScope,
      ...supplyResourceQueryKey(SupplyQueryResource.Tailored),
      'detail',
      id,
    ],
    enabled: Boolean(id),
    queryFn: () => client.getTailoredSupply(id ?? ''),
    refetchOnMount: 'always',
  });
}
export function useSupplyPlatformsQuery() {
  const client = useSupplyClient();
  return useQuery({
    queryKey: [...client.queryScope, ...supplyResourceQueryKey(SupplyQueryResource.Platforms)],
    queryFn: client.listPlatforms,
  });
}
export function useSupplyIabCategoriesQuery() {
  const client = useSupplyClient();
  return useQuery({
    queryKey: [...client.queryScope, ...supplyResourceQueryKey(SupplyQueryResource.IabCategories)],
    queryFn: client.listIabCategories,
  });
}

export function useSupplyMutations() {
  const client = useSupplyClient();
  const queryClient = useQueryClient();
  const invalidateApps = () =>
    queryClient.invalidateQueries({
      queryKey: [...client.queryScope, ...supplyResourceListsQueryKey(SupplyQueryResource.Apps)],
    });
  const invalidateDomains = () =>
    queryClient.invalidateQueries({
      queryKey: [...client.queryScope, ...supplyResourceListsQueryKey(SupplyQueryResource.Domains)],
    });
  const invalidateTailoredSupply = () =>
    queryClient.invalidateQueries({
      queryKey: [
        ...client.queryScope,
        ...supplyResourceListsQueryKey(SupplyQueryResource.Tailored),
      ],
    });
  return {
    createApp: useMutation({
      mutationFn: (payload: SupplyAppPayload) => client.createApp(payload),
      onSuccess: invalidateApps,
    }),
    updateApp: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: SupplyAppPayload }) =>
        client.updateApp(id, payload),
      onSuccess: invalidateApps,
    }),
    createDomain: useMutation({
      mutationFn: (payload: SupplyDomainPayload) => client.createDomain(payload),
      onSuccess: invalidateDomains,
    }),
    updateDomain: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: SupplyDomainPayload }) =>
        client.updateDomain(id, payload),
      onSuccess: invalidateDomains,
    }),
    createTailoredSupply: useMutation({
      mutationFn: (payload: TailoredSupplyPayload) => client.createTailoredSupply(payload),
      onSuccess: invalidateTailoredSupply,
    }),
    updateTailoredSupply: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: TailoredSupplyPayload }) =>
        client.updateTailoredSupply(id, payload),
      onSuccess: invalidateTailoredSupply,
    }),
  };
}
